import { AppModule } from 'app/module';
import { JobContent } from 'module/jobContent/main';
import { JobHeader } from 'module/jobHeader/main';
import { TabNav } from 'module/tabNav/main';
import { MobileKeyfactBox } from 'module/mobileKeyfactBox/main';
import { trackGa4 } from 'util/googleAnalytics';
import { onConsentChange } from 'util/consent';
import { VisitedJobsHistory } from 'util/client-side-storage/visitedJobsHistory';
import Template from './template.hbs';
import './styles.scss';

export class JobDetailContent extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    ready() {
        onConsentChange(this.addJobToVisitedJobsCache.bind(this));
        this.addJobToVisitedJobsCache();
    }

    addJobToVisitedJobsCache() {
        if (this.props.jobDetail.id) {
            VisitedJobsHistory.addJob(this.props.jobDetail.id);
        }
    }

    domBindings() {
        this.dom.inner = this.dom.el.querySelector('.m-jobDetailContent__inner');
        this.dom.jobTitle = this.dom.el.querySelector('.m-jobHeader__jobTitle');
        this.dom.jobContent = this.dom.el.querySelector('.m-jobContent');
        this.dom.jobText = this.dom.el.querySelector('.m-jobContent__jobText');
        this.dom.jobHeader = this.dom.el.querySelector('.m-jobHeader');
        this.dom.logo = this.dom.el.querySelector('.m-logo');
        this.dom.tabNav = this.dom.el.querySelector('.m-jobDetailContent .m-tabNav');
        this.dom.printButton = this.dom.el.querySelector('.m-jobDetailContent__print');
        this.dom.printLabel = this.dom.el.querySelector('.m-jobDetailContent__printLabel');
        this.dom.print = this.dom.el.querySelector('.m-jobDetailContent__print');
        this.dom.mobileKeyfactBox = this.dom.el.querySelector('.m-mobileKeyfactBox');

        /* JobHeader, MobileKeyfactBox Tabnav and JobContent instances are needed in
         `getPropsFromDom()` which runs before `subs()`. */
        if (this.dom.jobHeader) {
            this.jobHeader = new JobHeader(this.dom.jobHeader);
        }

        if (this.dom.tabNav) {
            this.tabNav = new TabNav(this.dom.tabNav);
        }

        if (this.dom.jobContent) {
            this.jobContent = new JobContent(this.dom.jobContent);
        }

        if (this.dom.mobileKeyfactBox) {
            this.mobileKeyfactBox = new MobileKeyfactBox(this.dom.mobileKeyfactBox);
        }
    }

    getPropsFromDom() {
        let headerProps = {};
        let tabsProps = {};
        let jobProps = {};
        let mobileKeyfactBoxProps = {};

        if (this.jobHeader) {
            headerProps = this.jobHeader.getPropsFromDom();
        }

        if (this.tabNav) {
            tabsProps = this.tabNav.getPropsFromDom();
        }

        if (this.jobContent) {
            jobProps = this.jobContent.getPropsFromDom();
        }

        if (this.mobileKeyfactBox) {
            mobileKeyfactBoxProps = this.mobileKeyfactBox.getPropsFromDom();
        }

        const props = {
            active: !!this.dom.inner,
            isSeamless: this.dom.el.getAttribute('data-is-seamless') === 'true',
            showKeyfactBox: this.dom.el.getAttribute('data-show-keyfactbox') === 'true',
            jobDetail: {
                id: this.dom.el.getAttribute('data-id'),
                noCache: this.dom.el.getAttribute('data-nocache'),
                companySlug: this.dom.el.getAttribute('data-company-slug'),
                isChiffreJob: this.dom.el.getAttribute('data-chiffre') === 'true',
                isInactive: this.dom.el.getAttribute('data-isinactive') === 'true',
                isHomeoffice: this.dom.el.getAttribute('data-is-homeoffice') === 'true',
                hasSalaryRange: this.dom.el.getAttribute('data-has-salary-range') === 'true',
                isPushedJob: this.dom.el.getAttribute('data-is-pushed-job') === 'true',
                companyId: this.dom.el.getAttribute('data-company-id'),
                isTypeBrandingSolution: this.dom.el.getAttribute('data-branding-solution') === 'true',
                isTypeEmployerPage: this.dom.el.getAttribute('data-employer-page') === 'true',
                isTypeEmployerPagePlus: this.dom.el.getAttribute('data-employer-page-plus') === 'true',
                isTypeAgpBasic: this.dom.el.getAttribute('data-agp-basic') === 'true',
                isTypeAgpPlus: this.dom.el.getAttribute('data-agp-plus') === 'true',
                isSmartApplyJob: this.dom.el.getAttribute('data-is-smart-apply-job') === 'true',
                jobBundleLabel: this.dom.el.getAttribute('data-jobbundlelabel'),
                jobLevel: this.dom.el.getAttribute('data-job-level'),
                employmentTypes: this.dom.el.getAttribute('data-employment-types'),
                jobFields: this.dom.el.getAttribute('data-job-fields'),
            },
            isWatchlist: this.dom.print
                ? this.dom.print.classList.contains('m-jobDetailContent__print--isWatchlist') : false,
            eventCategory: document.documentElement.getAttribute('data-event-category'),
        };

        return { ...headerProps, ...mobileKeyfactBoxProps, ...props, ...tabsProps, ...jobProps };
    }

    domEvents() {
        if (this.dom.printButton) {
            this.dom.printButton.addEventListener('click', () => this.print());
        }
    }

    print() {
        window.print?.();
    }

    subEvents() {
        if (this.jobHeader) {
            this.jobHeader.events.on('removeCloseTriggerStyles', () => {
                this.events.emit('removeCloseTriggerStyles');
            });

            this.jobHeader.events.on('addCloseTriggerStyles', () => {
                this.events.emit('addCloseTriggerStyles');
            });

            this.jobHeader.events.on('addShareModalStyles', () => this.events.emit('addShareModalStyles'));
            this.jobHeader.events.on('removeShareModalStyles', () => this.events.emit('removeShareModalStyles'));
            this.jobHeader.events.on('pushState', () => this.events.emit('pushState'));
        }

        this.tabNav?.events.on('changeTab', (e) => this.toggleContent(e));

        this.propagateEvents(this.jobContent, 'jobContentLoaded');
        this.propagateEvents(this.jobContent, 'jobContentLoading');
        this.propagateEvents(this.jobContent, 'removeModalStyle');
        this.propagateEvents(this.jobContent, 'addModalStyle');
        this.propagateEvents(this.jobContent, 'openDeleteModal');
        this.propagateEvents(this.jobContent, 'showSnackbar');
        this.propagateEvents(this.jobContent, 'memoSaved');
        this.propagateEvents(this.jobContent, 'memoDeleted');
        this.propagateEvents(this.jobContent, 'showMemoOverlay');
        this.propagateEvents(this.jobContent, 'hideMemoOverlay');
    }

    isShareModalOpen() {
        return this.jobHeader.isShareModalOpen();
    }

    closeShareModal() {
        this.jobHeader.closeShareModal();
    }

    openShareModal() {
        this.jobHeader.openShareModal();
    }

    toggleContent(e) {
        const { newProps } = e.arguments[0];
        this.props.tabs = newProps.tabs;
        for (let i = 0; i < this.props.tabs.length; i += 1) {
            if (this.props.tabs[i].active && this.props.tabs[i].company) {
                if ((!this.props.jobDetail.isInactive) && (this.props.eventCategory !== 'page: preview')) {
                    let productType = '';

                    if (this.props.jobDetail.isTypeBrandingSolution) {
                        productType = 'branding.solution';
                    } else if (this.props.jobDetail.isTypeEmployerPage) {
                        productType = 'employer.page';
                    } else if (this.props.jobDetail.isTypeEmployerPagePlus) {
                        productType = 'Arbeitgeberprofil';
                    } else if (this.props.jobDetail.isTypeAgpBasic) {
                        productType = 'Arbeitgeberprofil Basic';
                    } else if (this.props.jobDetail.isTypeAgpPlus) {
                        productType = 'Arbeitgeberprofil';
                    }

                    trackGa4({
                        event: 'company_view',
                        view_type: 'Employer Tab',
                        product_type: productType,
                        company_id: this.props.jobDetail.companyId ?? null,
                    });
                }
            }
        }

        this.events.emit('toggleContent');
    }

    sendShowEvents() {
        let viewType = null;

        if (this.props.eventCategory === 'page: job') {
            viewType = 'Standalone';
        } else if (this.props.eventCategory === 'page: jobs') {
            viewType = 'Job Search';
        } else if (this.props.eventCategory === 'page: jobalarm') {
            viewType = 'Job Alarm';
        } else if (this.props.eventCategory === 'page: jobmatching') {
            viewType = 'Job Matching';
        } else if (this.props.eventCategory === 'page: watchlist') {
            viewType = 'Job Watchlist';
        } else if (this.props.eventCategory === 'page: company-follow') {
            viewType = 'Company Alarm';
        }

        if (this.props.tabs) {
            if ((!this.props.jobDetail.isInactive) && (this.props.eventCategory !== 'page: preview')) {
                trackGa4({
                    event: 'job_view',
                    job_id: this.props.jobDetail.id ?? null,
                    job_type: this.props.isSeamless ? 'SJA' : 'Standard',
                    view_type: viewType,
                    application_type: this.getApplicationType(),
                    product_type: this.props.jobDetail.jobBundleLabel ?? null,
                    display_features: this.getTrackingDisplayFeatures(),
                    job_fields: this.props.jobDetail.jobFields,
                    employment_types: this.props.jobDetail.employmentTypes,
                    job_levels: this.props.jobDetail.jobLevel,
                    company_id: this.props.jobDetail.isChiffreJob ? null : this.props.jobDetail.companyId,
                });
            }
        }
    }

    getApplicationType() {
        return this.props.jobDetail.isSmartApplyJob
            ? 'SmartBewerben'
            : 'External';
    }

    getTrackingDisplayFeatures() {
        const displayFeatures = [];
        if (this.props.jobDetail.isPushedJob) {
            displayFeatures.push('pushed_job');
        }
        if (this.props.jobDetail.isTypeEmployerPagePlus || this.props.jobDetail.isTypeBrandingSolution) {
            displayFeatures.push('insights');
        }
        if (this.props.jobDetail.isTypeBrandingSolution) {
            displayFeatures.push('videos');
        }
        if (this.props.jobDetail.hasSalaryRange) {
            displayFeatures.push('salary');
        }
        if (this.props.jobDetail.isHomeoffice) {
            displayFeatures.push('homeoffice');
        }

        return displayFeatures.join(', ');
    }

    allowPointerEvents() {
        if (this.jobContent) {
            this.jobContent.allowPointerEvents();
        }
    }

    deleteMemo() {
        if (this.jobContent) {
            this.jobContent.deleteMemo();
        }
    }

    saveMemo() {
        if (this.jobContent) {
            this.jobContent.saveMemo();
        }
    }

    memoContentSimilar() {
        if (this.jobContent) {
            return this.jobContent.memoContentSimilar();
        }
        return false;
    }

    getTextareaValue() {
        if (this.jobContent) {
            return this.jobContent.getTextareaValue();
        }
        return false;
    }

    getActiveTab() {
        if (this.tabNav) {
            return this.tabNav.getActiveTab();
        }
        return false;
    }
}
